import { ChangeEvent, FC, useEffect, useMemo } from 'react';

import { TermsSummary } from 'components/ui';
import { CountryCode } from 'libphonenumber-js';

import i18n from '../../../../../providers/Locale';
import usePlace from '../../../../../providers/Place/hooks';
import { BookingSettings } from '../../../../../types/booking';
import Button from '../../../../ui/Button';
import InputField from '../../../../ui/InputField';
import PhoneField from '../../../../ui/PhoneField';
import { BookingStateHelpers } from '../helpers';
import { FormInputsError, FormInputsValue } from '../index';
import style from '../style.module.scss';

type StepSecondProps = {
	bookingSettings: BookingSettings;
	formState: FormInputsValue;
	formError: FormInputsError;
	isLoading: boolean;
	isPayed: boolean;
	onInputChange: (value: string, name: string) => void;
	onInputFieldChange: (event: ChangeEvent<HTMLInputElement>) => void;
	onReserveBtnClick: () => void;
	onMarketingChange: (state: boolean) => void;
};

const HIDE_INVOICE_COUNTRY_LIST = ['UA'];
const ALLOW_ONLY_NUMBERS_COUNTRY_LIST = ['PL'];

const StepSecond: FC<StepSecondProps> = (props) => {
	const { place } = usePlace();

	const {
		isPayed,
		bookingSettings,
		formState,
		formError,
		isLoading,
		onInputChange,
		onInputFieldChange,
		onReserveBtnClick,
		onMarketingChange,
	} = props;
	const minPersonsCount = bookingSettings?.deposit?.minPersonsCount;
	const withPersonCount = !!minPersonsCount && Number(formState.personCount) <= minPersonsCount;

	const depositAmount = useMemo(
		() =>
			BookingStateHelpers.getDepositAmountByDate(
				bookingSettings.deposit,
				formState.time,
				formState.date
			),

		[formState.date, formState.time, bookingSettings.deposit]
	);

	const legalInfoName = useMemo(() => {
		if (!bookingSettings) return '';
		return bookingSettings.legalInfo?.name || '';
	}, [bookingSettings]);

	useEffect(() => {
		if (!isPayed && formState?.customerVAT) onInputChange('', 'customerVAT');
	}, [isPayed, formState?.customerVAT]);

	const onNipChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (
			/^[0-9]*$/.test(event.target.value) ||
			!ALLOW_ONLY_NUMBERS_COUNTRY_LIST.includes(place?.contactInfo.address.countryCode ?? '')
		) {
			onInputFieldChange(event);
		}
	};

	return (
		<>
			<div className={style.item}>
				<InputField
					label={i18n('your_name')}
					name="name"
					value={formState?.name}
					error={formError?.name}
					onChange={onInputFieldChange}
				/>
			</div>
			<div className={style.item}>
				<InputField
					label={i18n('email') + (bookingSettings.requiredEmailBooking ? '*' : '')}
					name="email"
					value={formState?.email}
					error={formError?.email}
					onChange={onInputFieldChange}
				/>
			</div>

			<div className={style.item}>
				<PhoneField
					defaultCountry={place?.contactInfo?.address?.countryCode?.toUpperCase() as CountryCode}
					label={i18n('phone')}
					name="phone"
					value={formState?.phone || ''}
					error={formError?.phone}
					onChange={onInputChange}
				/>
			</div>

			{place?.customize?.hideRequestCustomerVAT &&
				!HIDE_INVOICE_COUNTRY_LIST.includes(place.contactInfo.address.countryCode) &&
				isPayed && (
					<div className={style.item}>
						<InputField
							label={i18n('invoice.company.nip')}
							name="customerVAT"
							value={formState?.customerVAT}
							error={formError?.customerVAT}
							onChange={onNipChange}
						/>
					</div>
				)}

			<div className={style.item}>
				<InputField
					label={i18n('booking_comment')}
					name="comment"
					value={formState?.comment}
					error={formError?.comment}
					onChange={onInputFieldChange}
				/>
			</div>

			<TermsSummary
				className={style.terms}
				companyName={legalInfoName}
				onChange={onMarketingChange}
				checked={formState.allowMarketing}
			/>

			<div className={style.nextWrapper}>
				<div className={style.confirmationText}>
					{i18n(
						'booking_terms_privacy',
						{
							privacyLink: `${place?.domain}/privacy-policy`,
							termsLink: `${place?.domain}/terms-of-use`,
						},
						true
					)}
				</div>
				{(bookingSettings.deposit?.active && withPersonCount) ||
				!bookingSettings.deposit?.active ||
				!depositAmount ? (
					<Button
						fullWidth
						size={'large'}
						appearance={'primary'}
						onClick={onReserveBtnClick}
						loading={isLoading}>
						{i18n('reserve_table')}
					</Button>
				) : (
					<Button
						fullWidth
						size={'large'}
						appearance={'primary'}
						onClick={onReserveBtnClick}
						loading={isLoading}>
						{i18n('booking.paid.btn_pay', {
							amount: String(
								(depositAmount *
									(bookingSettings.deposit.perPerson
										? parseInt(formState.personCount ?? '1')
										: 1)) /
									100
							),
							currency: bookingSettings.deposit.currency,
						})}
					</Button>
				)}
			</div>
		</>
	);
};

export default StepSecond;
